import React from 'react';

import { Button, Icon, Stack, Text } from '@eluve/components';

/**
 * AlertTriangle warning block (title + detail) used by {@link ProvisionPrompt}
 * and the deep-link {@link TenantProvisionDialog}'s error state. Pure — no
 * hooks or data fetching.
 */
export const ProvisionWarning: React.FC<{ title: string; detail: string }> = ({
  title,
  detail,
}) => (
  <Stack
    className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-text-warning-subtle"
    gap={3}
    direction="horizontal"
  >
    <Icon name="AlertTriangle" />
    <Stack align="stretch">
      <Text color="inherit" weight="regular">
        {title}
      </Text>
      <Text color="inherit">{detail}</Text>
    </Stack>
  </Stack>
);

export type ProvisionPromptProps = {
  title: string;
  detail: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  running: boolean;
};

/**
 * Presentational confirmation panel shared by the command palette and the
 * deep-link {@link TenantProvisionDialog}: an AlertTriangle warning block
 * (title + detail) above a `Cancel` / confirm button row. Pure — no hooks or
 * data fetching. Callers own the provisioning work and pass `running` to drive
 * the button states (Cancel disabled, confirm spinner) while it is in flight.
 */
export const ProvisionPrompt: React.FC<ProvisionPromptProps> = ({
  title,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
  running,
}) => (
  <Stack className="p-6" gap={4} align="stretch">
    <ProvisionWarning title={title} detail={detail} />
    <Stack direction="horizontal" gap={2} justify="end">
      <Button variant="outline" onClick={onCancel} disabled={running}>
        Cancel
      </Button>
      <Button onClick={onConfirm} loading={running}>
        {confirmLabel}
      </Button>
    </Stack>
  </Stack>
);
